import { lazy, type ComponentType } from 'react';

// After a frontend redeploy, the in-session SPA still references the
// old chunk hashes (e.g. assets/device-detail-Yh3YmmXN.js). Those files
// no longer exist on the new server, so the dynamic import throws
// "TypeError: Failed to fetch dynamically imported module: ...".
// Reloading the tab pulls the new index.html (with new hashes) and
// recovers transparently.
//
// We guard against reload loops via sessionStorage: if a reload was
// attempted within RELOAD_GUARD_MS, we re-throw so the ErrorBoundary
// can show its UI instead of looping forever.

const RELOAD_GUARD_MS = 30_000;
const STORAGE_KEY = 'aviron:chunk-reload-attempt';

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

// Returns true if a reload was triggered. Caller should re-throw on false
// so the ErrorBoundary can take over.
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(STORAGE_KEY) ?? '0');
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode quota etc.) — try anyway.
  }
  window.location.reload();
  return true;
}

// Clears the reload guard so an explicit user action (e.g. clicking
// "Try Again" on the ErrorBoundary) can force another reload even if
// the last one was within RELOAD_GUARD_MS.
export function resetChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Drop-in replacement for React.lazy that auto-recovers from stale
// chunk hashes after a redeploy. Use for every lazy-loaded route.
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err) && reloadOnceForChunkError()) {
        // Reload is in flight; return a stub component that will never
        // mount (the page is unloading). Re-throwing would also work
        // but produces a confusing flash of the ErrorBoundary first.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
